import { useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import { LogOut } from 'lucide-react'
import { Sidebar } from '@/design/Sidebar'
import { TopBar } from '@/design/TopBar'
import { PageContainer } from '@/design'
import { Toaster } from '@/design/Toaster'
import { useUiStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { useMediaQuery } from '@/lib/useMediaQuery'
import { useSessionKeepAlive } from '@/lib/useSessionKeepAlive'
import { logout as apiLogout } from '@/lib/api/auth'
import { ViewOnlyBanner } from './ViewOnlyBanner'
import { HubTabs } from './HubTabs'

/** Authenticated shell: sidebar (rail on desktop, drawer ≤1024) + top bar + routed content. */
export function AppLayout() {
  useSessionKeepAlive()
  const navigate = useNavigate()
  const isDrawer = useMediaQuery('(max-width: 1023px)') // lg boundary (A1/§4.4)
  const { sidebarCollapsed, toggleSidebar, drawerOpen, setDrawerOpen } = useUiStore()
  const status = useAuthStore((s) => s.status)
  const logoutReason = useAuthStore((s) => s.logoutReason)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Forced-logout (e.g. session invalidated on another device) → bounce to login.
  useEffect(() => {
    if (status === 'anonymous') {
      navigate('/login', { replace: true, state: { reason: logoutReason } })
    }
  }, [status, logoutReason, navigate])

  const handleMenuClick = () => (isDrawer ? setDrawerOpen(true) : toggleSidebar())

  const doLogout = async () => {
    setConfirmOpen(false)
    await apiLogout()
    useAuthStore.getState().logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop rail */}
      {!isDrawer && (
        <div className="h-full flex-none">
          <Sidebar collapsed={sidebarCollapsed} onLogout={() => setConfirmOpen(true)} />
        </div>
      )}

      {/* Mobile drawer */}
      {isDrawer && (
        <Dialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm" />
            <Dialog.Content className="fixed inset-y-0 left-0 z-50 h-full outline-none data-[state=open]:animate-in">
              <Dialog.Title className="sr-only">Navigation</Dialog.Title>
              <Sidebar
                collapsed={false}
                onNavigate={() => setDrawerOpen(false)}
                onLogout={() => {
                  setDrawerOpen(false)
                  setConfirmOpen(true)
                }}
              />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}

      {/* Content column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onMenuClick={handleMenuClick} showBrand={isDrawer} onLogout={() => setConfirmOpen(true)} />
        <main className="min-h-0 flex-1 overflow-y-auto">
          <PageContainer>
            <ViewOnlyBanner />
            <HubTabs />
            <Outlet />
          </PageContainer>
        </main>
      </div>

      <Toaster />

      {/* Logout confirmation */}
      <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-slate-900/40" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-6 shadow-pop outline-none">
            <Dialog.Title className="flex items-center gap-2.5 text-headline-md font-bold text-slate-900">
              <LogOut size={20} className="text-danger" /> Sign Out
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-body-md text-slate-600">
              Are you sure you want to sign out?
            </Dialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setConfirmOpen(false)}
                className="rounded-md border border-slate-200 px-5 py-2.5 text-title-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={doLogout}
                className="rounded-md bg-danger px-5 py-2.5 text-title-sm font-semibold text-white hover:shadow-md"
              >
                Sign Out
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}
